/**
 * Reusable bottom sheet with swipe-to-dismiss. Drag the grab handle / header
 * down past a threshold to close; tap the backdrop to close. Slide-up entry.
 *
 * Drag is bound to the header grabber only, so scrollable / input content
 * inside the sheet keeps working normally.
 */
import { useRef, useState, type ReactNode } from 'react';
import { hapticSelection } from '../lib/telegram';

interface Props {
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

const DISMISS_THRESHOLD_PX = 90;

export function BottomSheet({ onClose, title, children }: Props): JSX.Element {
  const [dragY, setDragY] = useState(0);
  const startY = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent): void => {
    startY.current = e.touches[0]?.clientY ?? null;
  };
  const onTouchMove = (e: React.TouchEvent): void => {
    if (startY.current == null) return;
    const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
    if (dy > 0) setDragY(dy);
  };
  const onTouchEnd = (): void => {
    if (dragY > DISMISS_THRESHOLD_PX) {
      hapticSelection();
      onClose();
    } else {
      setDragY(0);
    }
    startY.current = null;
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-end bg-black/50" onClick={onClose}>
      <div
        className="animate-slide-up max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl bg-tg-bg p-5 shadow-xl"
        style={{
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragY ? 'none' : 'transform 200ms ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-tg-hint/40" aria-hidden />
          {title && <h2 className="mb-3 text-lg font-bold text-tg-text">{title}</h2>}
        </div>
        {children}
      </div>
    </div>
  );
}
